import React, { useEffect } from 'react';
import { Collapse } from 'uiKit/atoms/Collapse/Collapse';
import { Table } from 'uiKit/atoms/Table/Table';
import { TableContainer } from 'uiKit/atoms/TableContainer/TableContainer';
import { TableHead } from 'uiKit/atoms/TableHead/TableHead';
import { TableRow } from 'uiKit/atoms/TableRow/TableRow';
import { TableCell } from 'uiKit/atoms/TableCell/TableCell';
import { TableBody } from 'uiKit/atoms/TableBody/TableBody';
import { TablePagination } from 'uiKit/atoms/TablePagination/TablePagination';
import { Paper } from 'uiKit/atoms/Paper/Paper';
import { ThemeProvider } from 'uiKit/atoms/ThemeProvider/ThemeProvider';
import { TableFooter } from 'uiKit/atoms/TableFooter/TableFooter';
import { CircularProgress } from 'uiKit/atoms/CircularProgress/CircularProgress';
import { Box } from 'uiKit/atoms/Box/Box';
import { IconButton } from 'uiKit/atoms/IconButton/IconButton';
import { KeyboardArrowDown } from 'uiKit/atoms/KeyboardArrowDown/KeyboardArrowDown';
import { KeyboardArrowUp } from 'uiKit/atoms/KeyboardArrowUp/KeyboardArrowUp';
import { FirstPage } from 'uiKit/atoms/FirstPage/FirstPage';
import { LastPage } from 'uiKit/atoms/LastPage/LastPage';
import { KeyboardArrowLeft } from 'uiKit/atoms/KeyboardArrowLeft/KeyboardArrowLeft';
import { KeyboardArrowRight } from 'uiKit/atoms/KeyboardArrowRight/KeyboardArrowRight';
import { observer } from 'mobx-react-lite';
import theme from './theme';
import styles from './MyCharityBanners.module.scss';
import DottedMenu from './DottedMenu';
import { useStores } from 'stores';
import defaultImage from '../../assets/img/bns-logo-big.svg';

interface TablePaginationActionsProps {
  count: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => void;
}

const TablePaginationActions: React.FC<TablePaginationActionsProps> = ({ count, page, rowsPerPage, onPageChange }) => {
  const handleFirstPageButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
      <IconButton
        sx={{ padding: '5px' }}
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        <FirstPage />
      </IconButton>
      <IconButton
        sx={{ padding: '5px' }}
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        <KeyboardArrowLeft />
      </IconButton>
      <IconButton
        sx={{ padding: '5px' }}
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        <KeyboardArrowRight />
      </IconButton>
      <IconButton
        sx={{ padding: '5px' }}
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        <LastPage />
      </IconButton>
    </Box>
  );
};

function Row(props: { row: any }) {
  const { row } = props;
  const [open, setOpen] = React.useState<boolean>(false);

  return (
    <React.Fragment>
      <TableRow>
        <TableCell align="center" sx={{ padding: '0' }}>
          <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell component="th" scope="row" align="left">
          {row.title}
        </TableCell>
        <TableCell align="left">{row.id}</TableCell>
        <TableCell align="left">{row.draft ? 'Yes' : 'No'}</TableCell>
        <TableCell>
          <DottedMenu bannerId={row.id} />
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} />
        <TableCell colSpan={4} style={{ paddingBottom: 0, paddingTop: 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box margin={1}>
              <Table size="small" aria-label="description details">
                <TableBody>
                  <TableRow>
                    <TableCell align="left">Description</TableCell>
                    <TableCell align="left">{row.description}</TableCell>
                    <TableCell align="left">Image</TableCell>
                    <TableCell align="left">
                      <img src={row.img ? row.img : defaultImage} alt="Charity Banner Image" />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}

const CollapsibleTable: React.FC = observer(() => {
  const { 
    charityBannerStore: { banners, fetchBanners, isLoading, totalItems }
    } = useStores();

  const [page, setPage] = React.useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = React.useState<number>(5);

  useEffect(() => {
    fetchBanners();
  }, []);

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (isLoading) {
    return <CircularProgress disableShrink className="circular-progress" />;
  }

  const displayMessage = "You haven't created any Charity Banners yet";
  if (banners.length === 0) {
    return (
      <div className={styles.bns3_error_message_container}>
        <h1 className={styles.bns3_error_message}>{displayMessage}</h1>
      </div>
    );
  }

  const visibleRows = banners.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  return (
    <ThemeProvider theme={theme}>
      <TableContainer component={Paper} sx={{ overflow: 'auto', padding: '16px' }}>
        <Table aria-label="collapsible table" sx={{ tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow>
              <TableCell align="left" sx={{ width: '5%' }} />
              <TableCell align="left" className={styles.bns3_label} sx={{ width: '30%' }}>
                Title
              </TableCell>
              <TableCell align="left" className={styles.bns3_label} sx={{ width: '30%' }}>
                Sliders
              </TableCell>
              <TableCell align="left" className={styles.bns3_label} sx={{ width: '30%' }}>
                Draft
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map((row, index) => (
              <Row key={index} row={row} />
            ))}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, { label: 'All', value: totalItems }]}
                colSpan={12}
                count={totalItems}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                ActionsComponent={TablePaginationActions}
                SelectProps={{
                  IconComponent: KeyboardArrowDown,
                }}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    </ThemeProvider>
  );
});

export default CollapsibleTable;
